import {
  useActiveAccountId,
  useActiveProjectId,
} from '@frameio/shared/contexts';
import { useCurrentPlanTracking } from '@frameio/shared/plans/hooks/useCurrentPlanTracking';
import { useCurrentPageType } from '@frameio/shared/routing';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { ToastButton, ToastButtonGroup, useToast } from '@frameio/vapor';
import { Toast } from '../components/Toast';
import {
  useGetProjectNotificationOverrideStatusQuery,
  useGetProjectNotificationStatusQuery,
  useProjectNotificationsOverrideStatusFragment,
  useProjectNotificationsStatusFragment,
} from '../gql';
import TEXT from './generated/TEXT/useNotificationsOptIn';
import { useToggleProjectNotifications } from './useToggleProjectNotifications';

TEXT.DEFINE({
  /** Toast title prompting user to enable project notifications */
  enableNotifications: 'Enable Notifications for this Project?',
  /** Button label to enable notifications */
  enable: 'Enable',
});

type Action =
  | 'comment'
  | 'upload'
  | 'asset_assignee_update'
  | 'asset_status_update'
  | 'brand_validation';

/**
 * Custom React hook that manages the logic for displaying a notifications
 * opt-in prompt (toast) to the user.
 * The toast will only show if the project notifications are off AND the project
 * does not have a notification override.
 *
 * Note: The toast will not display on share pages.
 *
 * @returns {Object} - An object containing the `maybeShowNotificationsOptInToast` function.
 */
export const useNotificationsOptIn = () => {
  const { addToast } = useToast();

  const enableNotificationsTitle = TEXT.enableNotifications;
  const enableButtonLabel = TEXT.enable;

  const projectId = useActiveProjectId();
  const accountId = useActiveAccountId();
  const pageType = useCurrentPageType();
  const currentPlan = useCurrentPlanTracking();

  const projectNotificationsStatusFragment =
    useProjectNotificationsStatusFragment(projectId ?? null);
  useGetProjectNotificationStatusQuery({
    skip:
      !accountId || !projectId || projectNotificationsStatusFragment.complete,
    variables: accountId && projectId ? { accountId, projectId } : undefined,
  });

  const projectNotificationsOverrideStatusFragment =
    useProjectNotificationsOverrideStatusFragment(projectId ?? null);
  useGetProjectNotificationOverrideStatusQuery({
    skip:
      !accountId ||
      !projectId ||
      projectNotificationsOverrideStatusFragment.complete,
    variables: accountId && projectId ? { accountId, projectId } : undefined,
  });

  const {
    enableProjectNotificationsMutation,
    disableProjectNotificationsMutation,
  } = useToggleProjectNotifications({
    projectId,
  });

  const track = useTrackUnsafe();

  /**
   * @function maybeShowNotificationsOptInToast
   * This function attempts to display a notifications opt-in toast
   *
   * @param {Object} params - Parameters to control the toast display behavior.
   * @param {Action} params.action - String representing the action performed to
   * trigger the function
   * @param {string} [params.assetId] - Optional asset ID associated with the action.
   *
   * @example
   * const { maybeShowNotificationsOptInToast } = useNotificationsOptIn();
   *
   * const onClick = () => {
   *    maybeShowNotificationsOptInToast({ action: someAction });
   * }
   */
  const maybeShowNotificationsOptInToast = ({
    action,
    assetId,
  }: {
    action: Action;
    assetId?: string;
  }) => {
    if (
      !accountId ||
      !projectId ||
      projectNotificationsStatusFragment.data.notificationsEnabled ||
      pageType === 'shareReviewFinder' ||
      pageType === 'shareReviewReel' ||
      projectNotificationsOverrideStatusFragment.data
        .hasProjectNotificationsOverride
    ) {
      return;
    }

    track('notification-opt-in-displayed', {
      action,
      current_plan_tier: currentPlan?.current_plan_tier,
      project_id: projectId,
      asset_id: assetId,
    });

    addToast(
      (close, closeLabel) => (
        <Toast
          adornmentAfter={
            <ToastButtonGroup>
              <ToastButton
                data-testid="toast-alert--cta"
                onPress={() => {
                  enableProjectNotificationsMutation();

                  track('notification-opt-in-actioned', {
                    action,
                    current_plan_tier: currentPlan?.current_plan_tier,
                    project_id: projectId,
                    asset_id: assetId,
                    component_clicked: 'enable',
                  });

                  close();
                }}
              >
                {enableButtonLabel}
              </ToastButton>
            </ToastButtonGroup>
          }
          closeLabel={closeLabel}
          data-testid="notification-opt-in-toast"
          onRequestClose={() => {
            disableProjectNotificationsMutation({
              force: true,
              shouldShowToast: false,
            });

            track('notification-opt-in-actioned', {
              action,
              current_plan_tier: currentPlan?.current_plan_tier,
              project_id: projectId,
              asset_id: assetId,
              component_clicked: 'x',
            });

            close();
          }}
          title={enableNotificationsTitle}
        />
      ),
      /**
       * Ensure the toast is auto-dismissible and
       * doesn't repeat similar toasts in the stack
       * https://spectrum.adobe.com/page/toast/#Auto-dismissible
       */
      {
        id: 'notifications-opt-in-toast',
        duration: 5000, // 5 seconds
      }
    );
  };

  return { maybeShowNotificationsOptInToast };
};
